import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  TextField,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { searchParams, getParams } from "../../../utlis/helpers";
import { ACTIVITY_TREND, PERSON_DASHBOARD } from "../../../routes/routerPath";
import PersonInfoSection from "./PersonInfoSection";
import { CircularProgress } from "@mui/material";
import {
  getSensorData,
  getSensorMapping,
  assignSensorToSenior,
  getAlertHistory,
} from "../../../services/activityMonitoringApi";
import { getUserList } from "../../../services/websiteApi";
import { getCarePlan } from "../../../services/v5Api";
import { transformCarePlanResponse } from "../personDashboard/carePlan/carePlanUtils";
import ActivityAbnormalities from "../../common/ActivityAbnormalities";
import { userInfoFetch, formatName } from "../../../utlis/commonFunction";
import { getCurrentLocale } from "../../../utlis/locale";
import "./activitymonitoring.scss";

const ActivityMonitoring = () => {
  const navigate = useNavigate();
  const [sensorData, setSensorData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAlertHistory, setShowAlertHistory] = useState(false);
  const [alertHistory, setAlertHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [carePlanActivity, setCarePlanActivity] = useState(null);

  // Sensor Setup State
  const [assignedSensorId, setAssignedSensorId] = useState(null);
  const [inputSensorId, setInputSensorId] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [setupError, setSetupError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editSensorId, setEditSensorId] = useState("");
  const [editError, setEditError] = useState("");
  const [isEditVerifying, setIsEditVerifying] = useState(false);

  const [userInfo, setUserInfo] = useState(() => userInfoFetch());

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const { seniorId: seniorIdFromParams } = getParams();

  // Resolve senior UUID — consistent with PersonDashboard and other v5 API consumers.
  const seniorUuid = userInfo?.id || seniorIdFromParams;

  // Bootstrap user info from API when sessionStorage is empty (direct navigation)
  useEffect(() => {
    if (userInfo?.id) return;
    if (!seniorIdFromParams) return;

    let mounted = true;
    getUserList(1, 999)
      .then((res) => {
        const users = res?.data?.data || [];
        sessionStorage.setItem("userList", JSON.stringify(users));
        const idx = users.findIndex((u) => u.id === seniorIdFromParams);
        if (idx !== -1 && mounted) {
          sessionStorage.setItem("userIndex", idx);
          setUserInfo(users[idx]);
        }
      })
      .catch((err) => {
        console.error("Error bootstrapping user info:", err);
      });
    return () => {
      mounted = false;
    };
  }, [seniorIdFromParams, userInfo?.id]);

  // Check for assigned sensor
  useEffect(() => {
    let mounted = true;

    const checkSensorAssociation = async () => {
      if (!seniorUuid) {
        if (mounted) {
          setError("Senior ID not found.");
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);
      try {
        const response = await getSensorMapping(seniorUuid);
        if (mounted) {
          // Check for success (status usually 1 or true)
          const isSuccess = response.success || response.status === 1;

          if (isSuccess && response.data?.sensorId) {
            setAssignedSensorId(response.data.sensorId);
            // Don't turn off loading here.
            // allow fetchSensorData to handle the loading state transition
            // to avoid a flash of empty dashboard content.
          } else {
            setAssignedSensorId(null);
            setIsLoading(false); // Only turn off if we are showing Setup Screen
          }
        }
      } catch (err) {
        console.error("Error checking sensor:", err);
        if (mounted) {
          setAssignedSensorId(null);
          setIsLoading(false);
        }
      }
    };

    checkSensorAssociation();

    return () => {
      mounted = false;
    };
  }, [seniorUuid]);

  // Fetch sensor data once we have an ID
  // silent = true skips loading spinner and ignores errors (used for background polling)
  const fetchSensorData = useCallback(
    async (silent = false) => {
      if (!assignedSensorId) return;

      if (!silent) {
        setIsLoading(true);
        setError(null);
      }
      try {
        const response = await getSensorData(assignedSensorId);
        if (response.success) {
          setSensorData(response.data);
          if (silent) setError(null);
        } else if (!silent) {
          if (response.error === "Not found") {
            setError("sensor_not_found");
          } else {
            setError("Failed to load sensor data");
          }
        }
      } catch (err) {
        console.error("Error fetching sensor data:", err);
        if (!silent) {
          const errMsg = err?.response?.data?.error;
          const status = err?.response?.status;
          if (errMsg === "Not found" || status === 404) {
            setError("sensor_not_found");
          } else {
            setError("Failed to load sensor data. Please try again.");
          }
        }
      } finally {
        if (!silent) {
          setIsLoading(false);
        }
      }
    },
    [assignedSensorId],
  );

  // Trigger fetch when assignedSensorId changes (initial load and new assignment)
  useEffect(() => {
    if (assignedSensorId) {
      fetchSensorData();
    }
  }, [assignedSensorId, fetchSensorData]);

  // Poll sensor data every 60 seconds for live status updates
  useEffect(() => {
    if (!assignedSensorId) return;

    const intervalId = setInterval(() => {
      fetchSensorData(true);
    }, 60000);

    return () => clearInterval(intervalId);
  }, [assignedSensorId, fetchSensorData]);

  // Fetch Alert history when history panel is opened
  useEffect(() => {
    if (showAlertHistory && assignedSensorId) {
      let mounted = true;
      const fetchHistory = async () => {
        setIsLoadingHistory(true);
        try {
          // Fetch page 1, limit 10 as per requirements
          const response = await getAlertHistory(assignedSensorId, 1, 10);
          if (mounted && response.success) {
            setAlertHistory(response.data || []);
          }
        } catch (err) {
          console.error("Error fetching alert history:", err);
        } finally {
          if (mounted) setIsLoadingHistory(false);
        }
      };
      fetchHistory();
      return () => {
        mounted = false;
      };
    }
  }, [showAlertHistory, assignedSensorId]);

  // Fetch care plan data for activity abnormalities
  useEffect(() => {
    let mounted = true;
    const loadCarePlanActivity = async () => {
      if (!seniorUuid) return;
      try {
        const response = await getCarePlan(seniorUuid);
        const apiData = response?.data || response;
        const transformed = transformCarePlanResponse(apiData);
        if (mounted && transformed?.insights?.activity) {
          setCarePlanActivity(transformed.insights.activity);
        }
      } catch (err) {
        console.error("Error loading care plan activity:", err);
      }
    };
    loadCarePlanActivity();
    return () => {
      mounted = false;
    };
  }, [seniorUuid]);

  const handleBack = () => {
    navigate(PERSON_DASHBOARD + searchParams);
  };

  const handleVerifyAndLink = async () => {
    if (!inputSensorId.trim()) {
      setSetupError("Please enter a Sensor ID.");
      return;
    }

    setIsVerifying(true);
    setSetupError("");

    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (!seniorUuid) {
        setSetupError("Cannot link: Missing Senior ID.");
        setIsVerifying(false);
        return;
      }

      const response = await assignSensorToSenior(seniorUuid, inputSensorId);

      // Check for success (status usually 1 or true)
      const isSuccess = response.success || response.status === 1;

      if (isSuccess) {
        setAssignedSensorId(inputSensorId);
        // fetchSensorData will trigger via useEffect
        setSnackbarMessage("Sensor linked successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } else {
        setSetupError(response.message || "Failed to link sensor.");
      }
    } catch (err) {
      console.error("Link error:", err);
      setSetupError("An error occurred while linking the sensor.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleOpenEditDialog = () => {
    setEditSensorId(assignedSensorId || "");
    setEditError("");
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditSensorId("");
    setEditError("");
  };

  const handleEditSave = async () => {
    if (!editSensorId.trim()) {
      setEditError("Please enter a Sensor ID.");
      return;
    }

    setIsEditVerifying(true);
    setEditError("");

    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (!seniorUuid) {
        setEditError("Cannot link: Missing Senior ID.");
        setIsEditVerifying(false);
        return;
      }

      const response = await assignSensorToSenior(seniorUuid, editSensorId);

      const isSuccess = response.success || response.status === 1;

      if (isSuccess) {
        setAssignedSensorId(editSensorId);
        setSnackbarMessage("Sensor updated successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        handleCloseEditDialog();
      } else {
        setEditError(response.message || "Failed to update sensor.");
      }
    } catch (err) {
      console.error("Edit sensor error:", err);
      setEditError("An error occurred while updating the sensor.");
    } finally {
      setIsEditVerifying(false);
    }
  };

  const renderEditSensorDialog = () => (
    <Dialog
      open={editDialogOpen}
      onClose={handleCloseEditDialog}
      maxWidth="sm"
      fullWidth
      className="edit-sensor-dialog"
    >
      <DialogTitle>
        <div className="dialog-title-row">
          <span>Edit Sensor Mapping</span>
          <IconButton onClick={handleCloseEditDialog} size="small">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                fill="currentColor"
              />
            </svg>
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <Typography className="edit-dialog-description">
          Update the Sensor ID mapped to this resident.
        </Typography>
        <TextField
          label="Sensor ID"
          variant="outlined"
          fullWidth
          value={editSensorId}
          onChange={(e) => {
            const val = e.target.value.replace(/[^a-zA-Z0-9\-_]/g, "");
            if (val.length <= 30) {
              setEditSensorId(val);
            }
          }}
          className="setup-input"
          error={!!editError}
          helperText={editError}
          autoFocus
          inputProps={{ autoComplete: "off" }}
        />
      </DialogContent>
      <DialogActions className="edit-dialog-actions">
        <button className="btn btn-outline" onClick={handleCloseEditDialog}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleEditSave}
          disabled={isEditVerifying}
        >
          {isEditVerifying ? (
            <CircularProgress size={20} style={{ color: "#FFF" }} />
          ) : (
            "Save"
          )}
        </button>
      </DialogActions>
    </Dialog>
  );

  const getAlertTypeLabel = (type) => {
    const labels = {
      fall_detected: "Fall Detected",
      fall_recovered: "Fall Recovered",
      pfall: "Possible Fall",
      none: "None",
    };
    return labels[type] || "None";
  };

  const getAlertTypeClass = (type) => {
    const classes = {
      fall_detected: "alert-detected",
      fall_recovered: "alert-recovered",
      pfall: "alert-suspected",
      none: "alert-none",
    };
    return classes[type] || "alert-none";
  };

  const getResidentStatusLabel = (status) => {
    if (!status) return "Unknown";
    const labels = {
      couch_in: "In Couch",
      couchin: "In Couch",
      bed_in: "In Bed",
      bedin: "In Bed",
      bed_exit: "Out of Bed",
      bed_out: "Out of Bed",
      presence: "Room",
      vacant: "Vacant",
      mount_angle_skewed: "Mount Angle Skewed",
      fall_detected: "Fall Detected",
      fall_recovered: "Fall Recovered",
    };
    return labels[status] || status.replace(/_/g, " ");
  };

  const getResidentStatusClass = (status) => {
    const classes = {
      couchin: "status-normal",
      couch_in: "status-normal",
      bedin: "status-normal",
      bed_in: "status-normal",
      bed_exit: "status-normal",
      bed_out: "status-normal",
      presence: "status-normal",
      vacant: "status-normal",
      mount_angle_skewed: "status-device-warning",
      fall_detected: "status-fall-ongoing",
      fall_recovered: "status-fall-recovered",
    };
    return classes[status] || "status-normal";
  };

  const getResidentStatusIcon = (status) => {
    if (status === "couchin" || status === "couch_in") {
      return (
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M35 20V15C35 12.24 32.76 10 30 10H10C7.24 10 5 12.24 5 15V20C3.34 20 2 21.34 2 23V28H5V32H8V28H32V32H35V28H38V23C38 21.34 36.66 20 35 20ZM10 20V15H30V20H10Z"
            fill="currentColor"
          />
        </svg>
      );
    }
    if (status === "bedin" || status === "bed_in") {
      return (
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M5 25V12H8V22H35V25H5Z" fill="currentColor" />
          <path
            d="M10 18C10 16.34 11.34 15 13 15H15C16.66 15 18 16.34 18 18V22H10V18Z"
            fill="currentColor"
          />
          <path d="M8 25V28H11V25H8ZM32 25V28H35V25H32Z" fill="currentColor" />
        </svg>
      );
    }
    if (status === "mount_angle_skewed") {
      return (
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 21H23L12 2L1 21ZM13 18H11V16H13V18ZM13 14H11V10H13V14Z"
            fill="currentColor"
          />
        </svg>
      );
    }
    if (status === "fall_detected" || status === "fall_recovered") {
      return (
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
            fill="currentColor"
          />
        </svg>
      );
    }
    return (
      <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="12" cy="8" r="4" fill="currentColor" />
        <path
          d="M12 14C8.68 14 2 15.68 2 19V22H22V19C22 15.68 15.32 14 12 14Z"
          fill="currentColor"
        />
      </svg>
    );
  };

  const getDeviceStatus = () => {
    if (!sensorData?.sensor) return "offline";
    return sensorData.sensor.status || "offline";
  };

  const getDeviceStatusBadge = () => {
    const status = getDeviceStatus();
    if (status === "online")
      return <span className="device-badge online">Online</span>;
    if (status === "offline")
      return <span className="device-badge offline">Offline</span>;
    return null;
  };

  const renderHeader = (extraRight) => (
    <div className="pinned-header">
      <div className="header-left">
        <button className="back-btn" onClick={handleBack} aria-label="Back">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M19 12H5m0 0l7-7m-7 7l7 7"
              stroke="#000000"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <Typography className="header-title">
          Fall & Activity Monitoring
        </Typography>
      </div>
      <div className="header-right">{extraRight}</div>
    </div>
  );

  const formatAlertTime = (isoString) => {
    if (!isoString) return "N/A";
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="activity-monitoring-wrapper">
        {renderHeader()}
        <div
          className="loading-container"
          style={{ margin: "auto", marginTop: "2rem" }}
        >
          <CircularProgress size={32} />
        </div>
      </div>
    );
  }

  // Sensor Setup UI (if no sensor assigned)
  if (!assignedSensorId && !isLoading) {
    return (
      <div className="activity-monitoring-wrapper">
        {renderHeader()}
        <div className="activity-content">
          <div className="sensor-setup-card">
            <Typography className="setup-title">Sensor Setup</Typography>
            <Typography className="setup-description">
              No sensor is currently associated with this resident. Please enter
              the Sensor ID to link it.
            </Typography>

            <TextField
              label="Sensor ID"
              variant="outlined"
              fullWidth
              value={inputSensorId}
              onChange={(e) => {
                const val = e.target.value.replace(/[^a-zA-Z0-9\-_]/g, "");
                if (val.length <= 30) {
                  setInputSensorId(val);
                }
              }}
              className="setup-input"
              error={!!setupError}
              helperText={setupError}
              inputProps={{ autoComplete: "off" }}
            />

            <button
              className="btn btn-primary"
              onClick={handleVerifyAndLink}
              disabled={isVerifying}
            >
              {isVerifying ? (
                <CircularProgress size={24} style={{ color: "#FFF" }} />
              ) : (
                "Verify & Link Sensor"
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Normal Dashboard (if sensor assigned and data loaded)
  // Or Error state if fetching failed
  if (error) {
    const isSensorNotFound = error === "sensor_not_found";
    return (
      <div className="activity-monitoring-wrapper">
        {renderHeader()}
        <div className="error-container">
          {isSensorNotFound ? (
            <>
              <Typography className="error-text">
                Sensor not found. The mapped Sensor ID may be incorrect.
              </Typography>
              <Typography className="error-subtext">
                Current: {assignedSensorId}
              </Typography>
              <button
                className="btn btn-primary"
                onClick={handleOpenEditDialog}
              >
                Change Sensor ID
              </button>
              <button className="btn btn-outline" onClick={fetchSensorData}>
                Retry
              </button>
            </>
          ) : (
            <>
              <Typography className="error-text">{error}</Typography>
              <button className="btn btn-primary" onClick={fetchSensorData}>
                Retry
              </button>
            </>
          )}
        </div>
        {renderEditSensorDialog()}
      </div>
    );
  }

  const lastAlert = sensorData?.lastAlert;
  const residentStatus = sensorData?.residentStatus?.current;

  return (
    <div className="activity-monitoring-wrapper">
      {renderHeader(getDeviceStatusBadge())}

      <PersonInfoSection
        personName={formatName(userInfo, getCurrentLocale()) || "Unknown"}
        gender={userInfo?.gender || ""}
        age={userInfo?.age || 0}
        room={userInfo?.room || ""}
        sensorId={assignedSensorId}
        onEditSensor={handleOpenEditDialog}
      />

      <div className="activity-content">
        <div className="activity-card alert-card">
          <Typography className="card-label">Last Alert:</Typography>
          <div className="alert-info">
            <Typography className="alert-time">
              {lastAlert ? formatAlertTime(lastAlert.time) : "No alerts"}
            </Typography>
            {lastAlert && (
              <Typography
                className={`alert-type ${getAlertTypeClass(lastAlert.type)}`}
              >
                {getAlertTypeLabel(lastAlert.type)}
              </Typography>
            )}
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setShowAlertHistory(!showAlertHistory)}
          >
            {showAlertHistory ? "Hide History" : "View History"}
          </button>

          {showAlertHistory && (
            <div className="history-panel">
              <Typography className="panel-title">Alert History</Typography>
              <div className="history-list">
                {isLoadingHistory ? (
                  <div
                    className="loading-history"
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "2rem",
                    }}
                  >
                    <CircularProgress size={24} />
                  </div>
                ) : alertHistory.length > 0 ? (
                  alertHistory.map((alertItem, index) => (
                    <div className="history-item" key={alertItem.id || index}>
                      <Typography className="history-time">
                        {formatAlertTime(alertItem.received_at)}
                      </Typography>
                      <Typography
                        className={`history-type ${getAlertTypeClass(alertItem.event_type)}`}
                      >
                        {getAlertTypeLabel(alertItem.event_type)}
                      </Typography>
                    </div>
                  ))
                ) : (
                  <Typography className="no-data">No alert history</Typography>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="activity-card abnormality-card">
          <Typography className="card-label">Abnormality</Typography>
          <ActivityAbnormalities
            fallAlerts={carePlanActivity?.fallAlerts}
            abnormalities={carePlanActivity?.abnormalities || []}
          />
        </div>

        <div className="activity-card status-card">
          <Typography className="card-label">Resident is currently:</Typography>
          <div className="status-info">
            {getDeviceStatus() === "offline" ? (
              <div className="device-badge offline">Offline</div>
            ) : (
              <>
                <div className="status-icon">
                  {getResidentStatusIcon(residentStatus)}
                </div>
                <Typography
                  className={`status-type ${getResidentStatusClass(residentStatus)}`}
                >
                  {getResidentStatusLabel(residentStatus)}
                </Typography>
              </>
            )}
          </div>
          <button
            className="btn btn-primary"
            onClick={() => navigate(ACTIVITY_TREND + searchParams)}
          >
            View Activity Trend
          </button>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {renderEditSensorDialog()}
    </div>
  );
};

export default ActivityMonitoring;
